import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import apiService from "../../services/apiService";

const statusDot = (status = "") => {
  const s = status.toLowerCase();
  const color = s.includes("active") ? "var(--vz-success)"
    : s.includes("never")           ? "var(--vz-secondary-color)"
    : "var(--vz-danger)";
  return (
    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: color, marginRight: 5, flexShrink: 0 }} />
  );
};

const AgentGroups = () => {
  document.title = "Group Policy — Access Control";

  const [agents,        setAgents]        = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [groups,        setGroups]        = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [form,         setForm]         = useState({ name: "", description: "", agent_ids: [] });
  const [editingGroup, setEditingGroup] = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const [alert, setAlert] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const res = await apiService.get("/api/wazuh/agents");
      setAgents(res.agents || []);
    } catch { /* ignore */ }
    finally { setLoadingAgents(false); }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      const res = await apiService.get("/api/agent-groups");
      if (res?.success) setGroups(res.groups || []);
    } catch { /* ignore */ }
    finally { setLoadingGroups(false); }
  }, []);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const agentOptions = agents.map((a) => ({
    value:  a.id,
    label:  `${a.name || a.id} (${a.ip || "—"})`,
    status: a.status,
  }));

  const startEdit = (g) => {
    setEditingGroup(g);
    setForm({
      name:        g.name,
      description: g.description || "",
      agent_ids:   agentOptions.filter((o) => g.agent_ids.includes(o.value)),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingGroup(null);
    setForm({ name: "", description: "", agent_ids: [] });
  };

  const saveGroup = async () => {
    const name = form.name.trim();
    if (!name) { showAlert("warning", "Group name is required."); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        description: form.description.trim(),
        agent_ids:   form.agent_ids.map((o) => o.value),
      };
      const res = editingGroup
        ? await apiService.put(`/api/agent-groups/${editingGroup.id}`, payload)
        : await apiService.post("/api/agent-groups", payload);
      if (res?.success) {
        await loadGroups();
        cancelEdit();
        showAlert("success", editingGroup ? `"${name}" updated successfully.` : `Group "${name}" created.`);
      } else {
        showAlert("danger", res?.error || "Failed to save group.");
      }
    } catch (err) {
      showAlert("danger", err?.message || "Failed to reach API.");
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiService.delete(`/api/agent-groups/${deleteTarget.id}`);
      if (res?.success) {
        await loadGroups();
        showAlert("success", `Group "${deleteTarget.name}" deleted.`);
      } else {
        showAlert("danger", res?.error || "Failed to delete group.");
      }
    } catch (err) {
      showAlert("danger", err?.message || "Failed to reach API.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const alertColors = {
    success: { color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.1)", border: "rgba(var(--vz-success-rgb),.3)", icon: "check-circle" },
    danger:  { color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.1)",  border: "rgba(var(--vz-danger-rgb),.3)",  icon: "alert-circle"  },
    warning: { color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.1)", border: "rgba(var(--vz-warning-rgb),.3)", icon: "alert-triangle" },
  };

  const inputStyle = {
    width:        "100%",
    padding:      "9px 12px",
    borderRadius: 8,
    border:       "1px solid var(--vz-border-color)",
    background:   "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
    color:        "var(--vz-body-color)",
    fontSize:     13,
    outline:      "none",
    boxSizing:    "border-box",
    transition:   "border-color .15s",
  };

  const selectStyles = {
    control: (b, s) => ({
      ...b,
      background:   "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
      borderColor:  s.isFocused ? "var(--vz-primary)" : "var(--vz-border-color)",
      boxShadow:    "none",
      minHeight:    38,
      fontSize:     13,
    }),
    menu:        (b) => ({ ...b, background: "var(--vz-secondary-bg, #fff)", zIndex: 9999 }),
    option:      (b, s) => ({
      ...b,
      background: s.isSelected ? "var(--vz-primary)" : s.isFocused ? "rgba(var(--vz-primary-rgb),.08)" : "transparent",
      color:      s.isSelected ? "#fff" : "var(--vz-body-color)",
      fontSize:   13,
    }),
    multiValue:       (b) => ({ ...b, background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: 6 }),
    multiValueLabel:  (b) => ({ ...b, color: "var(--vz-body-color)", fontSize: 12 }),
    multiValueRemove: (b) => ({ ...b, color: "var(--vz-secondary-color)", ":hover": { background: "rgba(var(--vz-danger-rgb),.1)", color: "var(--vz-danger)" } }),
    placeholder:      (b) => ({ ...b, color: "var(--vz-secondary-color)", fontSize: 13 }),
    input:            (b) => ({ ...b, color: "var(--vz-body-color)" }),
    formatOptionLabel: undefined,
  };

  const card = {
    background:   "var(--vz-secondary-bg, #fff)",
    border:       "1px solid var(--vz-border-color)",
    borderRadius: 12,
    boxShadow:    "0 1px 4px rgba(0,0,0,.06)",
  };

  const labelStyle = {
    fontSize:        11,
    fontWeight:      700,
    textTransform:   "uppercase",
    letterSpacing:   ".06em",
    color:           "var(--vz-secondary-color)",
    display:         "block",
    marginBottom:    6,
  };

  return (
    <div className="page-content">
      <Container fluid>

        {/* Page header */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h4 style={{ fontWeight: 700, margin: 0, color: "var(--vz-body-color)", display: "flex", alignItems: "center", gap: 10 }}>
                  <FeatherIcon icon="users" size={20} style={{ color: "var(--vz-primary)" }} />
                  Agent Groups
                </h4>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--vz-secondary-color)" }}>
                  Organise agents into named groups. Select a group in Web Filtering or App Blocking to target them instantly.
                </p>
              </div>
              <button
                onClick={cancelEdit}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "9px 18px", borderRadius: 8, border: "none",
                  background: editingGroup ? "transparent" : "var(--vz-primary)",
                  color: editingGroup ? "var(--vz-secondary-color)" : "#fff",
                  // eslint-disable-next-line
                  border: editingGroup ? "1px solid var(--vz-border-color)" : "none",
                  fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
              >
                {editingGroup ? (
                  <><FeatherIcon icon="plus" size={14} /> New Group</>
                ) : (
                  <><FeatherIcon icon="plus" size={14} /> New Group</>
                )}
              </button>
            </div>
            <hr style={{ margin: "16px 0 0", borderColor: "var(--vz-border-color)" }} />
          </Col>
        </Row>

        {/* Inline alert */}
        {alert && (() => {
          const c = alertColors[alert.type] || alertColors.warning;
          return (
            <Row className="mb-3">
              <Col xs={12}>
                <div style={{ padding: "10px 14px", borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, color: c.color, display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <FeatherIcon icon={c.icon} size={14} />
                  <span style={{ flex: 1 }}>{alert.message}</span>
                  <button onClick={() => setAlert(null)} style={{ background: "none", border: "none", cursor: "pointer", color: c.color, fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                </div>
              </Col>
            </Row>
          );
        })()}

        <Row className="g-4">

          {/* ── Left column: form ── */}
          <Col lg={4} xs={12}>
            <div style={{ ...card, padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "var(--vz-body-color)" }}>
                  {editingGroup ? "Edit Group" : "Create Group"}
                </h6>
                {editingGroup && (
                  <button onClick={cancelEdit}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", fontSize: 18, lineHeight: 1, padding: 0 }}>
                    ×
                  </button>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Group Name <span style={{ color: "var(--vz-danger)" }}>*</span></label>
                  <input
                    style={inputStyle}
                    placeholder="e.g. London Office, Finance Team"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && saveGroup()}
                    disabled={saving}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Description <span style={{ color: "var(--vz-secondary-color)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    style={inputStyle}
                    placeholder="e.g. All agents in the London branch"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Agents</label>
                    {!loadingAgents && (
                      <span style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>
                        {form.agent_ids.length} selected
                      </span>
                    )}
                  </div>
                  <Select
                    isMulti
                    isLoading={loadingAgents}
                    options={agentOptions}
                    value={form.agent_ids}
                    onChange={(v) => setForm((f) => ({ ...f, agent_ids: v || [] }))}
                    placeholder="Search and select agents…"
                    styles={selectStyles}
                    isDisabled={saving}
                    formatOptionLabel={(opt) => (
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {statusDot(opt.status)}
                        {opt.label}
                      </span>
                    )}
                  />
                  <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>
                    Online agents are shown with a green dot.
                  </p>
                </div>

                <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
                  <button
                    onClick={saveGroup}
                    disabled={saving || !form.name.trim()}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      padding: "10px 0", borderRadius: 8, border: "none",
                      cursor: saving || !form.name.trim() ? "not-allowed" : "pointer",
                      background: saving || !form.name.trim() ? "rgba(var(--vz-primary-rgb),.4)" : "var(--vz-primary)",
                      color: "#fff", fontWeight: 700, fontSize: 13,
                    }}
                  >
                    {saving ? <Spinner size="sm" /> : <FeatherIcon icon={editingGroup ? "save" : "plus-circle"} size={14} />}
                    {saving ? "Saving…" : editingGroup ? "Save Changes" : "Create Group"}
                  </button>
                  {editingGroup && (
                    <button onClick={cancelEdit}
                      style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-secondary-color)", cursor: "pointer", fontSize: 13 }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Col>

          {/* ── Right column: groups list ── */}
          <Col lg={8} xs={12}>
            {loadingGroups ? (
              <div style={{ ...card, padding: 40, textAlign: "center" }}>
                <Spinner color="primary" />
                <p style={{ margin: "12px 0 0", color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading groups…</p>
              </div>
            ) : groups.length === 0 ? (
              <div style={{ ...card, padding: "60px 40px", textAlign: "center" }}>
                <FeatherIcon icon="users" size={40} style={{ color: "var(--vz-secondary-color)", opacity: 0.2, display: "block", margin: "0 auto 16px" }} />
                <h6 style={{ fontWeight: 700, color: "var(--vz-body-color)", marginBottom: 6 }}>No agent groups yet</h6>
                <p style={{ color: "var(--vz-secondary-color)", fontSize: 13, margin: 0 }}>
                  Create your first group using the form on the left. Groups let you target sets of agents with a single click in Web Filtering and App Blocking.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {groups.map((g) => {
                  const isEditing = editingGroup?.id === g.id;
                  const groupAgents = agents.filter((a) => g.agent_ids.includes(a.id));
                  const onlineCount = groupAgents.filter((a) => (a.status || "").toLowerCase().includes("active")).length;

                  return (
                    <div
                      key={g.id}
                      style={{
                        ...card,
                        padding: "18px 20px",
                        borderLeft: isEditing ? "3px solid var(--vz-primary)" : "3px solid transparent",
                        transition: "border-color .2s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(var(--vz-primary-rgb),.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <FeatherIcon icon="users" size={15} style={{ color: "var(--vz-primary)" }} />
                            </div>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--vz-body-color)" }}>{g.name}</span>
                              {g.description && (
                                <p style={{ margin: "1px 0 0", fontSize: 12, color: "var(--vz-secondary-color)" }}>{g.description}</p>
                              )}
                            </div>
                          </div>

                          {/* Agent count badges */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(var(--vz-primary-rgb),.08)", color: "var(--vz-primary)" }}>
                              <FeatherIcon icon="cpu" size={10} />
                              {g.agent_ids.length} agent{g.agent_ids.length !== 1 ? "s" : ""}
                            </span>
                            {g.agent_ids.length > 0 && (
                              <>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(var(--vz-success-rgb),.1)", color: "var(--vz-success)" }}>
                                  <FeatherIcon icon="check-circle" size={10} />
                                  {onlineCount} online
                                </span>
                                {g.agent_ids.length - onlineCount > 0 && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "rgba(var(--vz-danger-rgb),.08)", color: "var(--vz-danger)" }}>
                                    <FeatherIcon icon="x-circle" size={10} />
                                    {g.agent_ids.length - onlineCount} offline
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          {/* Agent name chips */}
                          {groupAgents.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                              {groupAgents.map((a) => (
                                <span key={a.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, padding: "3px 9px", borderRadius: 6, border: "1px solid var(--vz-border-color)", background: "var(--vz-secondary-bg, #fff)", color: "var(--vz-body-color)" }}>
                                  {statusDot(a.status)}
                                  {a.name || a.id}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => startEdit(g)}
                            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1px solid var(--vz-border-color)", background: isEditing ? "rgba(var(--vz-primary-rgb),.08)" : "transparent", color: isEditing ? "var(--vz-primary)" : "var(--vz-body-color)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            <FeatherIcon icon="edit-2" size={12} />
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteTarget(g)}
                            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "none", background: "rgba(var(--vz-danger-rgb),.08)", color: "var(--vz-danger)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            <FeatherIcon icon="trash-2" size={12} />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>

      </Container>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTarget} toggle={() => !deleting && setDeleteTarget(null)} centered size="sm">
        <ModalHeader toggle={() => !deleting && setDeleteTarget(null)} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
          Delete Agent Group
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(var(--vz-danger-rgb),.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FeatherIcon icon="alert-triangle" size={18} style={{ color: "var(--vz-danger)" }} />
            </div>
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "var(--vz-body-color)" }}>
                Delete &ldquo;{deleteTarget?.name}&rdquo;?
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "var(--vz-secondary-color)", lineHeight: 1.5 }}>
                This removes the group definition only. Agents in this group are not affected and remain active.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid var(--vz-border-color)", gap: 8 }}>
          <button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-secondary-color)", cursor: "pointer", fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={deleteGroup}
            disabled={deleting}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--vz-danger)", color: "#fff", cursor: deleting ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 13, opacity: deleting ? 0.7 : 1 }}>
            {deleting ? <Spinner size="sm" /> : <FeatherIcon icon="trash-2" size={13} />}
            {deleting ? "Deleting…" : "Delete Group"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AgentGroups;
